import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/AdminHeader';
import { getContactInfo, updateContactInfo } from '../../services/api';
import { toast } from 'sonner';
import { Loader, Save, ArrowLeft } from 'lucide-react';

const AdminContactEdit = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    social: {
      instagram: '',
      twitter: '',
      linkedin: ''
    }
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      const data = await getContactInfo();
      setFormData({
        email: data.email || '',
        phone: data.phone || '',
        social: data.social || { instagram: '', twitter: '', linkedin: '' }
      });
    } catch (error) {
      console.error('Error fetching contact info:', error);
      toast.error('Failed to load contact info');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSocialChange = (e) => {
    setFormData({
      ...formData,
      social: { ...formData.social, [e.target.name]: e.target.value }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await updateContactInfo(formData);
      toast.success('Contact info updated successfully!');
      navigate('/admin');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to update contact info');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f]">
        <AdminHeader />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400 text-lg">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f]">
      <AdminHeader />

      <main className="max-w-[1000px] mx-auto px-8 py-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 group"
        >
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">BACK</span>
        </button>

        <h1 className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-purple-600 font-['Poppins'] text-5xl font-black mb-8">
          EDIT CONTACT INFO
        </h1>

        <form onSubmit={handleSubmit} className="glass-effect rounded-2xl p-8 space-y-6 border border-white/10">
          {/* Email */}
          <div>
            <label className="block text-white font-['Poppins'] text-xl font-semibold mb-2">
              EMAIL ADDRESS *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="hello@example.com"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all text-lg"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-white font-['Poppins'] text-xl font-semibold mb-2">
              PHONE NUMBER (OPTIONAL)
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+1 234 567 8900"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all text-lg"
            />
          </div>

          {/* Social Links */}
          <div className="glass-effect border border-purple-500/30 rounded-xl p-6">
            <h3 className="text-white font-['Poppins'] text-xl font-bold mb-4">
              SOCIAL MEDIA LINKS
            </h3>
            
            <div className="space-y-4">
              {/* Instagram */}
              <div>
                <label className="block text-gray-300 font-semibold mb-2">
                  Instagram Username
                </label>
                <input
                  type="text"
                  name="instagram"
                  value={formData.social.instagram}
                  onChange={handleSocialChange}
                  placeholder="your_instagram"
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all"
                />
              </div>

              {/* Twitter */}
              <div>
                <label className="block text-gray-300 font-semibold mb-2">
                  Twitter/X Username
                </label>
                <input
                  type="text"
                  name="twitter"
                  value={formData.social.twitter}
                  onChange={handleSocialChange}
                  placeholder="your_twitter"
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all"
                />
              </div>

              {/* LinkedIn */}
              <div>
                <label className="block text-gray-300 font-semibold mb-2">
                  LinkedIn Username
                </label>
                <input
                  type="text"
                  name="linkedin"
                  value={formData.social.linkedin}
                  onChange={handleSocialChange}
                  placeholder="your-linkedin"
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all"
                />
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-purple-500/30"
            >
              {submitting ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  SAVING...
                </>
              ) : (
                <>
                  <Save size={20} />
                  SAVE CHANGES
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="px-6 py-3 bg-white/5 border border-white/10 text-gray-300 rounded-lg hover:bg-white/10 transition-all"
            >
              CANCEL
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AdminContactEdit;